/* Taking undelivered medication back.
 *
 * The screen leads with the question a courier at the end of a shift actually
 * has: what is still in my van, and where does it go. The destination is the
 * heading, not a field to fill in, because the rule already knows: origin
 * while that pharmacy is open, the after-hours pharmacy once it has shut.
 *
 * The count is asked for and not pre-filled, the same as a pickup, and for the
 * same reason: a pre-filled number turns "confirm the count" into "tap
 * continue". A mismatch, or a load brought somewhere the rule did not expect,
 * needs a reason and is then allowed through. A courier holding medication
 * they cannot hand back is worse than a record with an explanation on it.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { Loading } from '../../app/Loading';
import { Pager, usePaged } from '../../app/Pager';
import { Section } from '../../app/Section';
import { sendOrQueue, uploadPhotoNow } from '../../lib/outbox';
import { PHOTO_ACCEPT, photoType, photoProblem } from '../../lib/photos';
import { CardButton, CardLink, Chip, Context, Panel, Screen, ScreenHeader, Sheet } from '../../app/Glass';
import { ReturnIcon, RouteIcon } from '../../app/Icons';

interface CarriedOrder {
    orderId: number; recipientName: string; externalRef: string;
    packages: number; from: string; failureReason: string;
}
interface Destination {
    site: { id: number; code: string; name: string };
    reason: 'origin' | 'after_hours' | 'after_hours_site_missing';
    why: string;
    orders: CarriedOrder[];
    packages: number;
}
interface Load {
    courierUsername: string;
    destinations: Destination[];
    totals: { orders: number; packages: number };
    notes: string[];
}
interface ReturnResult {
    returned: number[];
    refused: Array<{ orderId: number; error: string }>;
    discrepancy: number;
    notes: string[];
}

async function currentPosition(): Promise<{ lat: number; lng: number } | null> {
    if (!navigator.geolocation) return null;
    return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(null), 8000);
        navigator.geolocation.getCurrentPosition(
            (pos) => { clearTimeout(timer); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
            () => { clearTimeout(timer); resolve(null); },
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 30_000 },
        );
    });
}

export function Returns() {
    const { code = '' } = useParams();
    const base = `/api/projects/${code}/uh/returns`;

    const [load, setLoad] = useState<Load | null>(null);
    const [siteId, setSiteId] = useState<number | ''>('');
    const [signedName, setSignedName] = useState('');
    /* Part 3, photographed. One form covers everything going back. */
    const [formPhoto, setFormPhoto] = useState<File | null>(null);
    const [counted, setCounted] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState<ReturnResult | null>(null);
    const [queued, setQueued] = useState(false);
    const [msg, setMsg] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string; details?: string[] } | null>(null);

    const refresh = useCallback(async () => {
        try {
            const l = await api<Load>(base);
            setLoad(l);
            setSiteId((current) => (current === '' && l.destinations.length === 1 ? l.destinations[0]!.site.id : current));
        } catch (err) {
            setMsg({ kind: 'error', text: err instanceof ApiError ? err.message : 'Cannot reach dispatch.' });
            setLoad({ courierUsername: '', destinations: [], totals: { orders: 0, packages: 0 }, notes: [] });
        }
    }, [base]);
    useEffect(() => { void refresh(); }, [refresh]);

    /* ABOVE THE EARLY RETURN. The first render has no `load` and takes the
       spinner branch; a hook called only on the second is one React did not
       see on the first, and that is a white screen rather than a bad list.
       Same note as Pickup.tsx and app/src/screens/Run.tsx. */
    const handingBack = usePaged(load?.destinations.find((d) => d.site.id === siteId)?.orders ?? []);

    if (load === null) return <div className="izy-card"><Loading label="Loading what you are carrying" /></div>;

    const group = load.destinations.find((d) => d.site.id === siteId) ?? null;
    const expected = group?.packages ?? 0;
    const countedNumber = counted === '' ? null : Number(counted);
    const mismatch = countedNumber !== null && countedNumber !== expected;
    const ready = group !== null && signedName.trim() !== '' && formPhoto !== null && countedNumber !== null
        && (!mismatch || note.trim() !== '');

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        if (!ready || group === null) return;
        setBusy(true);
        setMsg(null);
        const position = await currentPosition();

        /* Uploaded before the return is recorded, and a failure records
           nothing: the same rule the door and the counter follow. */
        let courierFormFileId: number | undefined;
        try {
            courierFormFileId = await uploadPhotoNow({
                module: `/api/projects/${code}/uh`,
                orderId: null,
                blob: formPhoto!,
                contentType: photoType(formPhoto!) ?? "image/jpeg",
                kind: 'courier_form',
                idemKey: `return-${group.site.id}-${formPhoto!.size}-${formPhoto!.lastModified}`,
            });
        } catch (err) {
            setBusy(false);
            setMsg({
                kind: 'error',
                text: `The photo of the form could not be sent: ${err instanceof Error ? err.message : 'unknown error'}. `
                    + 'Nothing was recorded. Try again.',
            });
            return;
        }

        try {
            const outcome = await sendOrQueue({
                url: base,
                label: `Return at ${group.site.name}`,
                orderId: null,
                body: {
                    siteId: group.site.id,
                    signedName: signedName.trim(),
                    courierFormFileId,
                    countedPackages: countedNumber,
                    /* The orders are named explicitly. The courier is looking
                       at a list and handing over that list; letting the server
                       recompute the batch between the two would hand back
                       something the courier never saw. */
                    orderIds: group.orders.map((o) => o.orderId),
                    note: note.trim(),
                    ...(position ?? {}),
                },
            });
            if (outcome.sent) setDone(outcome.body as unknown as ReturnResult);
            else setQueued(true);
            setFormPhoto(null);
            setSignedName('');
            setCounted('');
            setNote('');
            setSiteId('');
            await refresh();
        } catch (err) {
            setMsg(err instanceof ApiError
                ? { kind: 'error', text: err.message, details: err.details }
                : { kind: 'error', text: 'Could not record the return. Try again.' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Screen>
            <Context>
                <ScreenHeader
                    title="Take back"
                    subtitle={<><Link to={`/projects/${code}/my-run`}>Today</Link> · undelivered packages</>}
                />
                <Panel>
                    <h2 className="izy-lede-kicker">Still with you</h2>
                    <p className="izy-lede">
                        {load.totals.orders} {load.totals.orders === 1 ? 'order' : 'orders'}
                    </p>
                    <p className="izy-lede-note">
                        {load.destinations.length === 0
                            ? 'Nothing undelivered is still with you.'
                            : 'These still count as attempted. Taking them back does not change that.'}
                    </p>
                    {load.destinations.length > 0 && (
                        <div className="izy-chips">
                            <Chip label={`${load.totals.packages} ${load.totals.packages === 1 ? 'package' : 'packages'}`} />
                            <Chip
                                label={`${load.destinations.length} ${load.destinations.length === 1 ? 'counter' : 'counters'}`}
                                tone="warn"
                            />
                        </div>
                    )}
                </Panel>
            </Context>

            <Sheet>
            {msg && (
                <div className={`izy-alert ${msg.kind}`} role={msg.kind === 'error' ? 'alert' : 'status'}>
                    {msg.text}
                    {msg.details && msg.details.length > 0 && <ul>{msg.details.map((d) => <li key={d}>{d}</li>)}</ul>}
                </div>
            )}

            {done && (
                <div className="izy-alert ok" role="status">
                    Handed back {done.returned.length} {done.returned.length === 1 ? 'order' : 'orders'}.
                    {done.notes.length > 0 && <ul>{done.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
                </div>
            )}

            {queued && (
                <div className="izy-alert warn" role="status">
                    Saved on this phone. The handover will be sent as soon as you have signal, and this list
                    will catch up then.
                </div>
            )}

            {load.destinations.length === 0 ? (
                <CardLink to={`/projects/${code}/my-run`} title="Back to today" Icon={RouteIcon} tone="primary" detail="Your round, and the next stop on it" />
            ) : (
                <form onSubmit={(e) => { void submit(e); }}>
                    <div className="izy-card">
                        {/* The count and the sentence moved into the panel
                            above: they say what this screen is for, and they
                            have to stay on screen while the form below is
                            filled in. What is left here is the choice. */}
                        <h2>Which counter</h2>
                        {load.destinations.map((d) => (
                            <label key={d.site.id} className="izy-choice">
                                <input
                                    type="radio"
                                    name="destination"
                                    value={d.site.id}
                                    /* Named explicitly. Without this the browser
                                       reads the site id off the value attribute
                                       and announces "6", which tells a courier
                                       using a screen reader nothing. */
                                    aria-label={`${d.site.name}, ${d.orders.length} ${d.orders.length === 1 ? 'order' : 'orders'}, ${d.packages} ${d.packages === 1 ? 'package' : 'packages'}. ${d.why}`}
                                    checked={siteId === d.site.id}
                                    onChange={() => { setSiteId(d.site.id); setCounted(''); setNote(''); }}
                                />
                                <span>
                                    <b>{d.site.name}</b>
                                    {' · '}{d.orders.length} {d.orders.length === 1 ? 'order' : 'orders'}
                                    {' · '}{d.packages} {d.packages === 1 ? 'package' : 'packages'}
                                    <br />
                                    <span className="izy-muted">{d.why}</span>
                                </span>
                            </label>
                        ))}
                        {load.destinations.some((d) => d.reason === 'after_hours_site_missing') && (
                            <div className="izy-alert warn" role="status">
                                No after-hours pharmacy is set for this project, so these fall back to where they came
                                from. If that pharmacy is shut, call dispatch rather than leaving them in the van.
                            </div>
                        )}
                    </div>

                    {group && (
                        <>
                            <div className="izy-card">
                                <h2>Count the packages</h2>
                                <p className="izy-muted">
                                    Count what you actually hand over the counter and type it in.
                                </p>
                                <label className="izy-field" style={{ maxWidth: 180 }}>Packages counted
                                    <input
                                        value={counted}
                                        onChange={(e) => setCounted(e.target.value.replace(/[^0-9]/g, ''))}
                                        inputMode="numeric"
                                        placeholder="0"
                                        required
                                    />
                                </label>
                                {mismatch && (
                                    <div className="izy-alert warn" role="status">
                                        These orders cover {expected}. Say why the count is different before you continue.
                                        <label className="izy-field" style={{ marginTop: 8 }}>Reason
                                            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="One box stayed at the front desk" />
                                        </label>
                                    </div>
                                )}
                                {/* Folded and paged, as on the pickup
                                    screen and for the same reason: at the
                                    counter the courier is counting boxes,
                                    and the names are what settles an
                                    argument about one of them. */}
                                <Section
                                    id="uh.returns.batch"
                                    title="What you are handing back"
                                    defaultOpen={false}
                                    summary={`${group.orders.length} ${group.orders.length === 1 ? 'order' : 'orders'}`}
                                >
                                    <ul className="izy-plain-list">
                                        {handingBack.rows.map((o) => (
                                            <li key={o.orderId}>
                                                {o.recipientName} · {o.packages} {o.packages === 1 ? 'package' : 'packages'}
                                                {o.externalRef && <> · {o.externalRef}</>}
                                                <br />
                                                <span className="izy-muted">from {o.from}{o.failureReason && <> · {o.failureReason}</>}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Pager of={handingBack} noun="orders" />
                                </Section>
                            </div>

                            <div className="izy-card">
                                <h2>The signed form</h2>
                                <p className="izy-muted">
                                    The printed name of the person taking them back, and a photograph of part 3
                                    of the docket they signed. One photograph covers everything going back.
                                </p>
                                <label className="izy-field">Printed name
                                    <input value={signedName} onChange={(e) => setSignedName(e.target.value)} required placeholder="Name of the pharmacy staff member" />
                                </label>

                                {/* THE PAD WENT ON 19 OCTOBER. Part 3 of the
                                    docket carries "PHARMACY USE ONLY - IF THIS
                                    DELIVERY WAS NOT COMPLETED" with the staff
                                    signature, date and time. That is what the
                                    counter signs when medication comes back,
                                    so a drawn mark here was a second
                                    signature for one handover. */}
                                <label className="izy-field">Photo of the signed form
                                    <input
                                        type="file"
                                        accept={PHOTO_ACCEPT}
                                        disabled={busy}
                                        onChange={(e) => {
                                        /* Caught here rather than by the
                                           server after the upload: a
                                           refusal at a counter should
                                           name the file, not arrive as
                                           a four-hundred. */
                                        const picked = e.target.files?.[0] ?? null;
                                        const problem = picked === null ? null : photoProblem(picked);
                                        if (problem !== null) {
                                            setMsg({ kind: 'error', text: problem });
                                            setFormPhoto(null);
                                            return;
                                        }
                                        setMsg(null);
                                        setFormPhoto(picked);
                                        }}
                                    />
                                    <span className="izy-muted izy-field-note">
                                        {formPhoto === null
                                            ? 'Part 3, signed by the pharmacy. Take one now, or choose one you already have.'
                                            : `Ready to send: ${formPhoto.name}`}
                                    </span>
                                </label>
                            </div>

                            <CardButton
                                submit
                                title={`Hand back ${group.orders.length} ${group.orders.length === 1 ? 'order' : 'orders'}`}
                                Icon={ReturnIcon}
                                tone="primary"
                                busy={busy}
                                disabled={!ready}
                                detail={ready
                                    ? `${counted} packages over the counter to ${signedName.trim()}`
                                    : [
                                        signedName.trim() === '' ? 'Add the printed name.' : '',
                                        formPhoto === null ? 'Photograph the signed form.' : '',
                                        countedNumber === null ? 'Count the packages.' : '',
                                        mismatch && note.trim() === '' ? 'Say why the count is different.' : '',
                                    ].filter(Boolean).join(' ')}
                            />
                        </>
                    )}
                </form>
            )}
            </Sheet>
        </Screen>
    );
}
